import { useState, useRef, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import {
	HiUsers,
	HiPlus,
	HiListBullet,
	HiEnvelope,
	HiExclamationTriangle,
	HiArrowLeft,
	HiMapPin,
	HiQuestionMarkCircle,
} from 'react-icons/hi2';

import { useActiveCampaignsForList } from '#emailCampaignFeature/hooks/useActiveCampaignsForList';
import Button from '#globalComponents/Button/Button';
import FormField from '#globalComponents/Forms/FormField';
import Select from '#globalComponents/Forms/Select';
import TextInput from '#globalComponents/Forms/TextInput';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';
import RadioButton from '#globalComponents/RadioButton/RadioButton';
import RolePriorityPicker from '#globalComponents/RolePriorityPicker/RolePriorityPicker';
import Tooltip from '#globalComponents/Tooltip/Tooltip';
import { useError } from '#globalContexts/ErrorContext';
import { sanitizeRolePriority } from '#lib/constants/contactRoles';
import { usePreferencesStore } from '#lib/stores/preferencesStore';

import { previewContactListRecipients } from '../../api/contactListsApi';
import { useCreateContactList, useAddContactsToList } from '../../models/mutations';
import { useContactLists } from '../../models/queries';

// A selection made in another table, resolved server-side: explicit ids or every row matching
// that table's filters (contextId = search query / campaign id where the table needs one).
const TABLE_SCOPE_SHAPE = PropTypes.shape({
	table: PropTypes.oneOf(['leads', 'crmCompanies', 'crmDeals', 'emailSends', 'postSends']).isRequired,
	contextId: PropTypes.string,
	ids: PropTypes.arrayOf(PropTypes.string),
	selectAll: PropTypes.bool,
	filters: PropTypes.object,
});

// Tables whose rows are companies: the user picks the recipient(s) per company first.
const COMPANY_SCOPE_TABLES = ['leads', 'crmCompanies'];

const COMPANY_LABELS = { singular: 'Firma', plural: 'Firmen' };
const CONTACT_LABELS = { singular: 'Kontakt', plural: 'Kontakte' };

// A selectAll scope is not a selection the user made row by row, so it is not called "ausgewählt".
const describeSource = (count, { singular, plural }, tableScope) => {
	const formatted = count.toLocaleString('de-DE');
	if (tableScope?.selectAll) return `Alle ${formatted} ${tableScope.filters ? 'gefilterten ' : ''}${plural}`;
	return `${formatted} ${count === 1 ? singular : plural} ausgewählt`;
};

// Company sources add a mode-dependent number of recipients per company (the preview step shows
// the exact total), so the list step names the companies instead of promising a contact count.
const describeRecipients = (count, countsCompanies) => {
	const formatted = count.toLocaleString('de-DE');
	if (countsCompanies) return `Empfänger aus ${formatted} ${count === 1 ? 'Firma' : 'Firmen'}`;
	return `${formatted} ${count === 1 ? 'Kontakt' : 'Kontakte'}`;
};

const recipientsReceivePhrase = (count, countsCompanies) => {
	if (countsCompanies) return `Die ${describeRecipients(count, true)} erhalten`;
	return count === 1 ? 'Der hinzugefügte Empfänger erhält' : `Die ${count} hinzugefügten Empfänger erhalten`;
};

const EMPTY_PREVIEW = {
	contactCount: 0,
	companyOnlyCount: 0,
	companyCount: 0,
	withEmailCount: 0,
	withAddressCount: 0,
	withNeitherCount: 0,
	skippedCount: 0,
};

/**
 * Live preview inside the recipient-mode step: one headline number (recipients), the channel
 * reachability as compact chips and warnings only when something is off. Composition
 * (contacts vs company recipients) is deliberately demoted to a muted subline — the user's
 * decision hinges on "how many, and can I reach them?", not on the recipient kind.
 */
const PreviewStats = ({ preview }) => {
	const {
		contactCount,
		companyOnlyCount,
		companyCount,
		withEmailCount,
		withAddressCount,
		withNeitherCount,
		skippedCount = 0,
	} = preview;
	const totalRecipients = contactCount + companyOnlyCount;

	const compositionParts = [];
	if (contactCount > 0) compositionParts.push(`${contactCount} Ansprechpartner`);
	if (companyOnlyCount > 0) {
		compositionParts.push(`${companyOnlyCount} ${companyOnlyCount === 1 ? 'Firmenkontakt' : 'Firmenkontakte'}`);
	}

	return (
		<div className="space-y-3">
			<div>
				<div className="flex items-baseline gap-2">
					<span className="text-lg font-bold leading-none text-primary-700">{totalRecipients}</span>
					<span className="text-sm text-primary-700">
						Empfänger aus {companyCount} {companyCount === 1 ? 'Firma' : 'Firmen'}
					</span>
				</div>
				{compositionParts.length > 0 && (
					<p className="text-xs text-primary-700/70 mt-1">{compositionParts.join(' · ')}</p>
				)}
			</div>

			<div className="flex flex-wrap gap-2">
				<span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-primary-200 px-2.5 py-1 text-xs font-medium text-primary-700">
					<HiEnvelope className="w-3.5 h-3.5 text-primary-700" />
					{withEmailCount} per E-Mail erreichbar
				</span>
				<span className="inline-flex items-center gap-1.5 rounded-full bg-white border border-primary-200 px-2.5 py-1 text-xs font-medium text-primary-700">
					<HiMapPin className="w-3.5 h-3.5 text-primary-700" />
					{withAddressCount} per Post erreichbar
				</span>
			</div>

			{withNeitherCount > 0 && (
				<div className="flex items-start gap-2">
					<HiExclamationTriangle className="w-4 h-4 text-caution-500 flex-shrink-0 mt-0.5" />
					<span className="text-xs text-caution-700">
						{withNeitherCount} {withNeitherCount === 1 ? 'Empfänger ist' : 'Empfänger sind'} weder per
						E-Mail noch per Post erreichbar — mit „Empfänger einschränken?“ ausschließbar
					</span>
				</div>
			)}
			{skippedCount > 0 && (
				<div className="flex items-start gap-2">
					<HiExclamationTriangle className="w-4 h-4 text-caution-500 flex-shrink-0 mt-0.5" />
					<span className="text-xs text-caution-700">
						{skippedCount} {skippedCount === 1 ? 'Firma wird' : 'Firmen werden'} übersprungen — kein
						Empfänger passt zur gewählten Rollen-Priorität bzw. Erreichbarkeit
					</span>
				</div>
			)}
		</div>
	);
};

PreviewStats.propTypes = {
	preview: PropTypes.shape({
		contactCount: PropTypes.number.isRequired,
		companyOnlyCount: PropTypes.number.isRequired,
		companyCount: PropTypes.number.isRequired,
		withEmailCount: PropTypes.number.isRequired,
		withAddressCount: PropTypes.number.isRequired,
		withNeitherCount: PropTypes.number.isRequired,
		skippedCount: PropTypes.number,
	}).isRequired,
};

/**
 * Step 1 for company-selection modes (company tableScope / crmCompanyIds): the user picks who to
 * add per company. Live-previews the resulting recipient counts so the choice is informed.
 */
const RecipientModeStep = ({
	crmCompanyIds,
	tableScope,
	selectionCount,
	recipientMode,
	onRecipientModeChange,
	rolePriority,
	onRolePriorityChange,
	reachableBy,
	onReachableByChange,
	onNext,
	onClose,
}) => {
	const [preview, setPreview] = useState(null);
	const [loading, setLoading] = useState(true);
	const { showError } = useError();

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		const body = { recipientMode };
		if (recipientMode === 'rolePriority') body.rolePriority = rolePriority;
		if (reachableBy !== 'all') body.reachableBy = reachableBy;
		if (tableScope) body.scope = tableScope;
		else if (crmCompanyIds?.length) body.crmCompanyIds = crmCompanyIds;

		(async () => {
			try {
				const result = await previewContactListRecipients(body);
				if (!cancelled) setPreview(result ?? EMPTY_PREVIEW);
			} catch (err) {
				if (!cancelled) {
					setPreview(EMPTY_PREVIEW);
					showError({
						title: 'Vorschau nicht verfügbar',
						message: err?.message || 'Die Empfänger-Vorschau konnte nicht geladen werden.',
					});
				}
			} finally {
				if (!cancelled) setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [crmCompanyIds, tableScope, recipientMode, rolePriority, reachableBy, showError]);

	const totalRecipients = preview ? preview.contactCount + preview.companyOnlyCount : 0;
	const companyCount = preview?.companyCount ?? selectionCount;

	return (
		<Modal isOpen onClose={onClose} size="m">
			<Panel.Header
				icon={HiUsers}
				onClose={onClose}
				subtitle={describeSource(companyCount, COMPANY_LABELS, tableScope)}
			>
				Empfänger zur Kontaktliste hinzufügen
			</Panel.Header>
			<Panel.Body>
				<div className="mb-4">
					<label className="block text-sm font-medium text-text mb-2">Wen hinzufügen?</label>
					<div className="space-y-1 border border-bgBase-400 rounded-lg p-3">
						<div className="flex items-center gap-2">
							<RadioButton
								name="recipientMode"
								value="rolePriority"
								checked={recipientMode === 'rolePriority'}
								onChange={onRecipientModeChange}
								label="Ein Empfänger pro Firma (nach Rollen-Priorität)"
								size="md"
							/>
							<Tooltip
								content={
									<div className="w-72 rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg">
										<p>
											Pro Firma wird der erste Ansprechpartner gemäß deiner Rollen-Reihenfolge
											gewählt. Bei mehreren Kontakten derselben Rolle gewinnt der als „primär“
											markierte, sonst der älteste Eintrag.
										</p>
										<p className="mt-1">
											Enthält die Liste „Firmenkontakt“, wird die Firma selbst als Empfänger
											verwendet, wenn keine der davor stehenden Rollen besetzt ist. Firmen ohne
											passenden Eintrag werden übersprungen.
										</p>
									</div>
								}
							>
								<button
									type="button"
									className="text-bgBase-800 hover:text-text"
									aria-label="Was bedeutet 'Rollen-Priorität'?"
								>
									<HiQuestionMarkCircle className="h-4 w-4 cursor-help" />
								</button>
							</Tooltip>
						</div>
						{recipientMode === 'rolePriority' && (
							<div className="mt-2 mb-1 rounded-lg border border-bgBase-400 bg-bgBase-200 p-3">
								<RolePriorityPicker value={rolePriority} onChange={onRolePriorityChange} />
							</div>
						)}
						<RadioButton
							name="recipientMode"
							value="allContacts"
							checked={recipientMode === 'allContacts'}
							onChange={onRecipientModeChange}
							label="Alle Ansprechpartner pro Firma"
							size="md"
						/>
					</div>
					<div className="mt-4">
						<div className="flex items-center gap-2 mb-2">
							<label className="block text-sm font-medium text-text">Empfänger einschränken?</label>
							<Tooltip
								content={
									<div className="w-72 rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg">
										<p>
											<strong>Nur mit E-Mail:</strong> Es zählen Ansprechpartner mit eigener
											E-Mail. Hat keiner eine, wird die Firma hinzugefügt, sofern sie selbst eine
											E-Mail hat.
										</p>
										<p className="mt-1">
											<strong>Nur mit Postadresse:</strong> Es zählen nur Firmen mit vollständiger
											Postadresse (Straße, PLZ, Stadt).
										</p>
										<p className="mt-1">Nicht erreichbare Firmen werden übersprungen.</p>
									</div>
								}
							>
								<button
									type="button"
									className="text-bgBase-800 hover:text-text"
									aria-label="Was bedeuten die Erreichbarkeits-Filter?"
								>
									<HiQuestionMarkCircle className="h-4 w-4 cursor-help" />
								</button>
							</Tooltip>
						</div>
						<div className="space-y-1 border border-bgBase-400 rounded-lg p-3">
							<RadioButton
								name="reachableBy"
								value="all"
								checked={reachableBy === 'all'}
								onChange={onReachableByChange}
								label="Alle Empfänger hinzufügen"
								size="md"
							/>
							<RadioButton
								name="reachableBy"
								value="email"
								checked={reachableBy === 'email'}
								onChange={onReachableByChange}
								label="Nur Empfänger mit E-Mail-Adresse (für E-Mail-Kampagnen)"
								size="md"
							/>
							<RadioButton
								name="reachableBy"
								value="post"
								checked={reachableBy === 'post'}
								onChange={onReachableByChange}
								label="Nur Empfänger mit Postadresse (für Post-Mailings)"
								size="md"
							/>
						</div>
					</div>
				</div>

				<div className="p-3 bg-primary-100 border border-primary-200 rounded-lg min-h-[88px]">
					{loading || !preview ? (
						<div className="flex items-center gap-2">
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 flex-shrink-0" />
							<span className="text-sm text-primary-700">Lade Vorschau...</span>
						</div>
					) : (
						<PreviewStats preview={preview} />
					)}
				</div>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" onClick={onClose}>
					Abbrechen
				</Button>
				<Button variant="solid" disabled={loading || totalRecipients === 0} onClick={onNext}>
					Weiter
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

RecipientModeStep.propTypes = {
	crmCompanyIds: PropTypes.arrayOf(PropTypes.string),
	tableScope: TABLE_SCOPE_SHAPE,
	selectionCount: PropTypes.number.isRequired,
	recipientMode: PropTypes.oneOf(['rolePriority', 'allContacts']).isRequired,
	onRecipientModeChange: PropTypes.func.isRequired,
	rolePriority: PropTypes.arrayOf(PropTypes.string).isRequired,
	onRolePriorityChange: PropTypes.func.isRequired,
	reachableBy: PropTypes.oneOf(['all', 'email', 'post']).isRequired,
	onReachableByChange: PropTypes.func.isRequired,
	onNext: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
};

const SubmitButtonLabel = ({ isBusy, contactCount, countsCompanies }) => {
	if (isBusy) {
		return (
			<>
				<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
				Hinzufügen...
			</>
		);
	}
	return (
		<>
			<HiUsers className="w-4 h-4 mr-2" />
			{describeRecipients(contactCount, countsCompanies)} hinzufügen
		</>
	);
};

SubmitButtonLabel.propTypes = {
	isBusy: PropTypes.bool,
	contactCount: PropTypes.number,
	countsCompanies: PropTypes.bool,
};

/**
 * List selection step (new or existing list) - shared across all modes
 */
const ListSelectionStep = ({
	contactCount,
	countsCompanies = false,
	sourceLabel,
	onSubmit,
	onClose,
	onBack,
	isSubmitting,
}) => {
	const [mode, setMode] = useState('existing'); // 'existing' | 'new'
	const [selectedListId, setSelectedListId] = useState('');
	const [newListName, setNewListName] = useState('');

	const inputRef = useRef(null);
	const { data: contactLists = [], isLoading: listsLoading } = useContactLists();
	const createListMutation = useCreateContactList();
	const { showError } = useError();

	useEffect(() => {
		if (mode === 'new' && inputRef.current) {
			const timer = setTimeout(() => inputRef.current?.focus(), 100);
			return () => clearTimeout(timer);
		}
	}, [mode]);

	const handleSubmit = async () => {
		if (mode === 'new') {
			if (!newListName.trim()) {
				showError({ title: 'Name fehlt', message: 'Bitte geben Sie einen Namen für die neue Liste ein.' });
				return;
			}
			try {
				const result = await createListMutation.mutateAsync({ name: newListName.trim() });
				onSubmit(result.data);
			} catch {
				showError({ title: 'Fehler', message: 'Fehler beim Erstellen der Liste.' });
			}
		} else {
			if (!selectedListId) {
				showError({ title: 'Keine Liste ausgewählt', message: 'Bitte wählen Sie eine Kontaktliste aus.' });
				return;
			}
			const list = contactLists.find((l) => l.id === selectedListId);
			onSubmit(list);
		}
	};

	return (
		<Modal isOpen onClose={onClose} size="m">
			<Panel.Header
				icon={HiUsers}
				onClose={onClose}
				subtitle={sourceLabel}
				actions={
					onBack && (
						<button
							type="button"
							onClick={onBack}
							aria-label="Zurück"
							className="w-8 h-8 rounded-control text-bgBase-800 hover:text-text hover:bg-bgBase-400 flex items-center justify-center transition-colors"
						>
							<HiArrowLeft className="w-5 h-5" />
						</button>
					)
				}
			>
				Kontakte zur Kontaktliste hinzufügen
			</Panel.Header>
			<Panel.Body>
				<div className="flex gap-2 mb-6">
					<button
						type="button"
						onClick={() => setMode('existing')}
						className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
							mode === 'existing'
								? 'bg-primary-100 border-primary-300 text-primary-700'
								: 'bg-white border-gray-200 text-text hover:bg-gray-50'
						}`}
					>
						<HiListBullet className="w-4 h-4" />
						Vorhandene Liste
					</button>
					<button
						type="button"
						onClick={() => setMode('new')}
						className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border text-sm font-medium transition-colors ${
							mode === 'new'
								? 'bg-primary-100 border-primary-300 text-primary-700'
								: 'bg-white border-gray-200 text-text hover:bg-gray-50'
						}`}
					>
						<HiPlus className="w-4 h-4" />
						Neue Liste erstellen
					</button>
				</div>

				{mode === 'existing' ? (
					<div className="space-y-2">
						<label className="text-sm font-medium text-text">Kontaktliste auswählen</label>
						{listsLoading ? (
							<div className="h-11 bg-gray-100 rounded-md animate-pulse" />
						) : contactLists.length === 0 ? (
							<div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
								<p className="text-sm text-bgBase-800 mb-2">Keine Kontaktlisten vorhanden.</p>
								<Button variant="outline" size="sm" onClick={() => setMode('new')}>
									Neue Liste erstellen
								</Button>
							</div>
						) : (
							<Select value={selectedListId} onChange={(e) => setSelectedListId(e.target.value)}>
								<option value="">Liste auswählen...</option>
								{contactLists.map((list) => (
									<option key={list.id} value={list.id}>
										{list.name} ({list.recipientCount} Empfänger)
									</option>
								))}
							</Select>
						)}
					</div>
				) : (
					<FormField
						label="Name der neuen Liste"
						htmlFor="newContactListName"
						hint={`${newListName.length}/100 Zeichen`}
					>
						<TextInput
							id="newContactListName"
							ref={inputRef}
							placeholder="z.B. Follow-Up Geöffnet, Re-Engagement April"
							value={newListName}
							onChange={(e) => setNewListName(e.target.value)}
							maxLength={100}
							disabled={isSubmitting || createListMutation.isPending}
						/>
					</FormField>
				)}

				{contactCount === 0 && (
					<div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mt-4 text-sm text-bgBase-800">
						Keine Kontaktpersonen gefunden. Die ausgewählten Einträge haben keine hinterlegten Kontakte.
					</div>
				)}

				<div className="bg-primary-100 border border-primary-200 rounded-lg p-3 mt-4">
					<p className="text-sm text-primary-700">
						Die Kontakte werden der Liste hinzugefügt und können anschließend für E-Mail- oder
						Post-Mailing-Kampagnen verwendet werden. Bereits vorhandene Kontakte werden nicht doppelt
						hinzugefügt.
					</p>
				</div>
			</Panel.Body>
			<Panel.Footer className="justify-end">
				<Button variant="outline" onClick={onClose} disabled={isSubmitting || createListMutation.isPending}>
					Abbrechen
				</Button>
				<Button
					variant="solid"
					onClick={handleSubmit}
					disabled={
						isSubmitting ||
						contactCount === 0 ||
						createListMutation.isPending ||
						(mode === 'existing' && !selectedListId) ||
						(mode === 'new' && !newListName.trim())
					}
				>
					<SubmitButtonLabel
						isBusy={isSubmitting || createListMutation.isPending}
						contactCount={contactCount}
						countsCompanies={countsCompanies}
					/>
				</Button>
			</Panel.Footer>
		</Modal>
	);
};

ListSelectionStep.propTypes = {
	contactCount: PropTypes.number.isRequired,
	countsCompanies: PropTypes.bool,
	sourceLabel: PropTypes.string.isRequired,
	onSubmit: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	onBack: PropTypes.func,
	isSubmitting: PropTypes.bool,
};

/**
 * Computes which of the four input modes is active. Pure; lives outside the component so the
 * boolean fan-out does not weigh on the component's cognitive complexity budget.
 *
 * @param {object} inputs
 * @returns {{ isScopeMode: boolean, isGlobalMode: boolean, isCompanyMode: boolean,
 *   hasRecipientModeStep: boolean }}
 */
const computeModeFlags = ({ tableScope, globalContactPersonIds, crmCompanyIds }) => {
	const isScopeMode = Boolean(tableScope);
	const isGlobalMode = (globalContactPersonIds?.length ?? 0) > 0;
	const isCompanyMode = (crmCompanyIds?.length ?? 0) > 0;
	return {
		isScopeMode,
		isGlobalMode,
		isCompanyMode,
		hasRecipientModeStep: COMPANY_SCOPE_TABLES.includes(tableScope?.table) || isCompanyMode,
	};
};

/**
 * Body of the confirmation shown before adding recipients to a list that a running campaign uses.
 * The wording deliberately avoids promising an immediate send: the backend schedules against the
 * campaign's sending window and daily cap, so "sent right now" would be wrong whenever the window
 * is closed.
 *
 * @param {object} props
 * @param {string} props.listName
 * @param {Array<{id: string, name: string}>} props.activeCampaigns
 * @param {number} props.contactCount
 * @param {boolean} [props.countsCompanies] - contactCount counts source companies, not recipients
 * @returns {JSX.Element}
 */
const ActiveCampaignWarning = ({ listName, activeCampaigns, contactCount, countsCompanies = false }) => {
	const isSingle = activeCampaigns.length === 1;

	return (
		<div>
			<p className="mb-3">
				Die Liste <span className="font-semibold">&quot;{listName}&quot;</span> wird von{' '}
				{isSingle ? (
					<>
						der laufenden Kampagne{' '}
						<span className="font-semibold">&quot;{activeCampaigns[0].name}&quot;</span>
					</>
				) : (
					<>
						<span className="font-semibold">{activeCampaigns.length} laufenden Kampagnen</span> (
						{activeCampaigns.map((campaign) => campaign.name).join(', ')})
					</>
				)}{' '}
				als Empfängerquelle genutzt.
			</p>
			<p className="text-sm font-medium text-text mb-2">Folgendes passiert:</p>
			<ul className="list-disc list-inside text-sm text-text space-y-1">
				<li>
					{recipientsReceivePhrase(contactCount, countsCompanies)} automatisch die E-Mails{' '}
					{isSingle ? 'dieser Kampagne' : 'dieser Kampagnen'}
				</li>
				<li>
					Der Versand startet, sobald das Sendefenster {isSingle ? 'der Kampagne' : 'der jeweiligen Kampagne'}{' '}
					es zulässt
				</li>
				<li>Empfänger ohne E-Mail-Adresse erhalten keine Nachricht</li>
			</ul>
		</div>
	);
};

ActiveCampaignWarning.propTypes = {
	listName: PropTypes.string,
	activeCampaigns: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired, name: PropTypes.string }))
		.isRequired,
	contactCount: PropTypes.number.isRequired,
	countsCompanies: PropTypes.bool,
};

/**
 * The full submit pipeline: flips the submitting flag, runs the add, shows the appropriate
 * toast, calls onSuccess and closes the modal. Lives outside the component so the try/catch/
 * finally branching does not count against the component's cognitive complexity budget.
 *
 * @param {object} params - see call site for shape
 * @returns {Promise<void>}
 */
const runSubmit = async ({
	contactList,
	modes,
	addContactsMutateAsync,
	setIsSubmitting,
	showSuccess,
	showError,
	onSuccess,
	handleClose,
}) => {
	setIsSubmitting(true);
	try {
		await addContactsMutateAsync(buildAddRecipientsBody({ contactListId: contactList.id, ...modes }));
		showSuccess({
			title: 'Empfänger hinzugefügt',
			message: `Empfänger wurden zur Liste "${contactList.name}" hinzugefügt.`,
			autoClose: true,
		});
		onSuccess?.();
		handleClose();
	} catch (error) {
		showError({
			title: error?.title || 'Fehler',
			message: error?.message || 'Die Kontakte konnten nicht zur Liste hinzugefügt werden.',
		});
	} finally {
		setIsSubmitting(false);
	}
};

/**
 * Builds the mutation body for the add request. Pure: takes the modal's
 * inputs and current recipientMode, returns the body the backend expects. Company sources
 * carry recipientMode (+ the ordered rolePriority list for that mode) + reachableBy (the
 * filter only exists on the recipient-mode step); contact sources do not. `reachableBy: 'all'`
 * means no restriction and is omitted.
 */
const buildAddRecipientsBody = ({
	contactListId,
	isScopeMode,
	isCompanyMode,
	isGlobalMode,
	tableScope,
	crmCompanyIds,
	globalContactPersonIds,
	contactPersonIds,
	recipientMode,
	rolePriority,
	reachableBy,
}) => {
	const modeBody = {
		recipientMode,
		...(recipientMode === 'rolePriority' ? { rolePriority } : {}),
		...(reachableBy === 'all' ? {} : { reachableBy }),
	};
	if (isScopeMode) {
		return COMPANY_SCOPE_TABLES.includes(tableScope.table)
			? { contactListId, scope: tableScope, ...modeBody }
			: { contactListId, scope: tableScope };
	}
	if (isCompanyMode) return { contactListId, crmCompanyIds, ...modeBody };
	if (isGlobalMode) return { contactListId, globalContactIds: globalContactPersonIds };
	return { contactListId, crmContactIds: contactPersonIds };
};

/**
 * Universal modal for adding recipients to a contact list.
 *
 * Supports four modes:
 * 1. globalContactPersonIds - Global contact IDs from leads (auto-imports to CRM)
 * 2. contactPersonIds       - CRM contact IDs (direct add)
 * 3. tableScope             - A selection in another table (explicit ids or all matching its
 *                             filters), resolved server-side. Company tables (leads, CRM companies)
 *                             get the recipient-mode step first. Pass `contactCount` = rows covered.
 * 4. crmCompanyIds          - CRM company IDs; same recipient-mode step as company scopes
 *
 * For modes 3 and 4 the modal renders a recipient-mode step first (with live preview counts),
 * then the list-selection step. All other modes go straight to list selection.
 */
const AddToContactListModal = ({
	isOpen,
	onClose,
	// Mode 1: Global contact IDs (from leads area — auto-imports to CRM)
	globalContactPersonIds,
	// Mode 2: Direct CRM contact IDs
	contactPersonIds,
	// Mode 3: Lead companies of a search query (one recipient per company via role priority)
	tableScope,
	// Mode 4: CRM company IDs (one recipient per company via role priority)
	crmCompanyIds,
	// Optional: contact count override for display
	contactCount: contactCountProp,
	// Optional: German noun of the source rows for the subtitle (default Kontakt/Kontakte;
	// company sources always read Firma/Firmen)
	entityLabels = CONTACT_LABELS,
	// Optional: callback after successful add
	onSuccess,
}) => {
	const { isScopeMode, isGlobalMode, isCompanyMode, hasRecipientModeStep } = computeModeFlags({
		tableScope,
		globalContactPersonIds,
		crmCompanyIds,
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [step, setStep] = useState(hasRecipientModeStep ? 'recipientMode' : 'listSelection');
	const [recipientMode, setRecipientMode] = useState('rolePriority');
	const [reachableBy, setReachableBy] = useState('all');

	// Last-used priority is persisted in the preferences store (shared with the CSV export).
	const storedRolePriority = usePreferencesStore((s) => s.contactRolePriority);
	const setContactRolePriority = usePreferencesStore((s) => s.setContactRolePriority);
	const rolePriority = useMemo(() => sanitizeRolePriority(storedRolePriority), [storedRolePriority]);

	const { showError, showSuccess } = useError();
	const addContactsMutation = useAddContactsToList();
	const { getActiveCampaignsForList, isLoading: loadingCampaigns } = useActiveCampaignsForList();

	// Set once the user picks a list that feeds a running campaign; drives the confirmation dialog.
	const [pendingAdd, setPendingAdd] = useState(null);

	// Company sources count companies; how many recipients they yield depends on the recipient
	// mode and is only known from the preview, so their labels name companies rather than contacts.
	const displayCount =
		contactCountProp ?? globalContactPersonIds?.length ?? crmCompanyIds?.length ?? contactPersonIds?.length ?? 0;
	const countsCompanies = hasRecipientModeStep;
	const sourceLabels = countsCompanies ? COMPANY_LABELS : entityLabels;

	// Reset modal-local state when reopened.
	useEffect(() => {
		if (isOpen) {
			setIsSubmitting(false);
			setRecipientMode('rolePriority');
			setReachableBy('all');
			setStep(hasRecipientModeStep ? 'recipientMode' : 'listSelection');
			setPendingAdd(null);
		}
	}, [isOpen, hasRecipientModeStep]);

	const handleClose = () => {
		onClose();
	};

	const submitToList = (contactList) =>
		runSubmit({
			contactList,
			modes: {
				isScopeMode,
				isCompanyMode,
				isGlobalMode,
				tableScope,
				crmCompanyIds,
				globalContactPersonIds,
				contactPersonIds,
				recipientMode,
				rolePriority,
				reachableBy,
			},
			addContactsMutateAsync: addContactsMutation.mutateAsync,
			setIsSubmitting,
			showSuccess,
			showError,
			onSuccess,
			handleClose,
		});

	// A list feeding a running campaign turns "add contacts" into "send emails". Confirm first.
	// A freshly created list has no campaigns, so that path submits straight through.
	const handleListSelected = (contactList) => {
		const activeCampaigns = getActiveCampaignsForList(contactList?.id);
		if (activeCampaigns.length === 0) return submitToList(contactList);

		setPendingAdd({ contactList, activeCampaigns });
		return undefined;
	};

	const handleConfirmAdd = () => {
		const { contactList } = pendingAdd;
		setPendingAdd(null);
		void submitToList(contactList);
	};

	if (!isOpen) return null;

	return (
		<>
			{step === 'recipientMode' ? (
				<RecipientModeStep
					crmCompanyIds={isCompanyMode ? crmCompanyIds : undefined}
					tableScope={isScopeMode ? tableScope : undefined}
					selectionCount={displayCount}
					recipientMode={recipientMode}
					onRecipientModeChange={setRecipientMode}
					rolePriority={rolePriority}
					onRolePriorityChange={setContactRolePriority}
					reachableBy={reachableBy}
					onReachableByChange={setReachableBy}
					onNext={() => setStep('listSelection')}
					onClose={handleClose}
				/>
			) : (
				<ListSelectionStep
					contactCount={displayCount}
					countsCompanies={countsCompanies}
					sourceLabel={describeSource(displayCount, sourceLabels, isScopeMode ? tableScope : undefined)}
					onSubmit={handleListSelected}
					onClose={handleClose}
					onBack={hasRecipientModeStep ? () => setStep('recipientMode') : undefined}
					// Block submitting until the campaigns are in: an empty lookup result would
					// otherwise read as "no active campaign" and skip the warning. A failed query
					// deliberately does not block — the add stays usable, only unwarned.
					isSubmitting={isSubmitting || loadingCampaigns}
				/>
			)}

			<ConfirmModal
				isOpen={Boolean(pendingAdd)}
				onClose={() => setPendingAdd(null)}
				onConfirm={handleConfirmAdd}
				title="Empfänger zu laufender Kampagne hinzufügen"
				message={
					pendingAdd && (
						<ActiveCampaignWarning
							listName={pendingAdd.contactList?.name}
							activeCampaigns={pendingAdd.activeCampaigns}
							contactCount={displayCount}
							countsCompanies={countsCompanies}
						/>
					)
				}
				subtitle={null}
				confirmText="Hinzufügen"
				cancelText="Abbrechen"
			/>
		</>
	);
};

AddToContactListModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	globalContactPersonIds: PropTypes.arrayOf(PropTypes.string),
	contactPersonIds: PropTypes.arrayOf(PropTypes.string),
	tableScope: TABLE_SCOPE_SHAPE,
	crmCompanyIds: PropTypes.arrayOf(PropTypes.string),
	contactCount: PropTypes.number,
	entityLabels: PropTypes.shape({ singular: PropTypes.string.isRequired, plural: PropTypes.string.isRequired }),
	onSuccess: PropTypes.func,
};

export default AddToContactListModal;
